import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { TextField } from '../components/Field';
import { Alert, PaymentBadge, Spinner, StatusBadge } from '../components/ui';
import { formatClock, formatDateShort, formatDateTime, labelFor } from '../lib/format';
import type {
  HeatAssignment,
  LookupResult,
  OwnEntry,
  PaymentInstructions,
} from '../lib/types';
import { useEventContext } from '../lib/useEvent';

export function LookupPage() {
  const { event } = useEventContext();
  const [reference, setReference] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ reference?: string; email?: string; form?: string }>({});
  const [loading, setLoading] = useState(false);
  const [found, setFound] = useState<LookupResult | null>(
    null,
  );

  async function onSubmit(formEvent: React.FormEvent) {
    formEvent.preventDefault();

    const next: typeof errors = {};
    if (!reference.trim()) next.reference = 'Enter your entry reference';
    if (!email.trim()) next.email = 'Enter the email address you entered with';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setLoading(true);
    setErrors({});
    setFound(null);
    try {
      setFound(await api.lookup(reference.trim(), email.trim()));
    } catch (err) {
      setErrors({
        form:
          err instanceof ApiError
            ? err.message
            : 'Could not look up your entry. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="prose stack">
      <h1>Find my entry</h1>
      <p className="muted">
        Enter the reference you were given when you signed up, along with the email address you used.
      </p>

      <form className="card stack" onSubmit={onSubmit} noValidate>
        <div className="form-grid">
          <TextField
            label="Entry reference"
            required
            placeholder="HB-XXXXXX"
            autoComplete="off"
            spellCheck={false}
            hint="Shown on screen when you entered."
            value={reference}
            error={errors.reference}
            onChange={(e) => {
              setReference(e.target.value.toUpperCase());
              setErrors((prev) => ({ ...prev, reference: undefined }));
            }}
          />
          <TextField
            label="Email address"
            type="email"
            required
            autoComplete="email"
            value={email}
            error={errors.email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
          />
        </div>

        <div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Spinner label="Searching" /> Searching...
              </>
            ) : (
              'Find my entry'
            )}
          </button>
        </div>
      </form>

      {errors.form && (
        <Alert variant="error" title="Not found">
          <p style={{ marginBottom: 0 }}>
            {errors.form} If you have lost your reference, email{' '}
            <a href={`mailto:${event?.contactEmail ?? ''}`}>
              {event?.contactEmail ?? 'the organisers'}
            </a>
            .
          </p>
        </Alert>
      )}

      {found && (
        <EntryPanel
          entry={found.entry}
          assignment={found.assignment}
          payment={found.payment ?? null}
        />
      )}
    </div>
  );
}

function EntryPanel({
  entry,
  assignment,
  payment,
}: {
  entry: OwnEntry;
  assignment: HeatAssignment | null;
  payment: PaymentInstructions | null;
}) {
  const { event } = useEventContext();
  const categoryLabel =
    event?.categories.find((c) => c.id === entry.categoryId)?.label ?? entry.categoryId;

  return (
    <section className="card stack" aria-label="Your entry">
      <div className="spread">
        <h2 style={{ margin: 0 }}>
          {entry.firstName} {entry.lastName}
        </h2>
        <span className="mono" style={{ fontWeight: 700, color: 'var(--deep)' }}>
          {entry.reference}
        </span>
      </div>

      <div className="row">
        <StatusBadge status={entry.status} />
        <PaymentBadge status={entry.paymentStatus} />
      </div>

      {entry.paymentStatus === 'unpaid' && entry.status !== 'withdrawn' && (
        <Alert variant="warning" title="Payment outstanding">
          {payment ? (
            // Newline separated by the API, same string as the confirmation screen.
            <p style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>{payment.instructions}</p>
          ) : (
            <p style={{ marginBottom: 0 }}>
              Your place is held but not yet confirmed. Quote {entry.reference} when you pay.
            </p>
          )}
        </Alert>
      )}

      {assignment ? (
        <Alert variant="success" title={assignment.heatName}>
          <p style={{ marginBottom: 0 }}>
            Lane / position <strong>{assignment.lane}</strong>
            {assignment.scheduledAt && (
              <>
                {' '}
                &middot; scheduled start <strong>{formatClock(assignment.scheduledAt)}</strong>
              </>
            )}
            . See the <Link to="/start-lists">full start lists</Link>.
          </p>
        </Alert>
      ) : (
        <p className="muted">
          Your start time has not been published yet. Check back closer to the event.
        </p>
      )}

      <dl className="grid grid-2" style={{ margin: 0 }}>
        <Row label="Predicted time" value={entry.seedTimeFormatted} />
        <Row label="Age band on event day" value={entry.ageGroup} />
        <Row label="Category" value={categoryLabel} />
        <Row label="Club" value={entry.club || 'None given'} />
        <Row label="Email" value={entry.email} />
        <Row label="Phone" value={entry.phone} />
        <Row label="Date of birth" value={formatDateShort(entry.dateOfBirth)} />
        <Row label="Gender" value={labelFor(entry.gender)} />
        <Row label="Emergency contact" value={entry.emergencyContactName} />
        <Row label="Emergency phone" value={entry.emergencyContactPhone} />
        <Row label="Medical notes" value={entry.medicalNotes || 'None given'} />
        <Row label="Entered" value={formatDateTime(entry.createdAt)} />
      </dl>

      <p className="faint" style={{ marginBottom: 0 }}>
        Need a change? Email{' '}
        <a href={`mailto:${event?.contactEmail ?? ''}`}>
          {event?.contactEmail ?? 'the organisers'}
        </a>{' '}
        quoting {entry.reference}. Details cannot be edited online.
      </p>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="field-label">{label}</dt>
      <dd className="muted" style={{ margin: 0 }}>
        {value}
      </dd>
    </div>
  );
}
